import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminCompanyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { Building2, MapPin, Loader2, ChevronRight } from 'lucide-react';

const statusTabs = [
  { key: '',         label: 'All'      },
  { key: 'PENDING',  label: 'Pending'  },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
];

const statusBadge = {
  PENDING:  'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

export default function AllCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminCompanyAPI.getCompanies(filter || undefined, 0, 50);
      const data = res.data.data;
      setCompanies(data.content || []);
      setTotal(data.totalElements || 0);
    } catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav title="All Companies" subtitle={`${total} registered`} />

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Status tabs */}
        <div className="flex gap-1.5 flex-wrap mb-6">
          {statusTabs.map((tab) => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition
                ${filter === tab.key
                  ? 'bg-slate-900 text-white shadow'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100">
            <Building2 className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No companies found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {companies.map((c) => (
              <div key={c.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5
                  flex items-center justify-between gap-4 cursor-pointer hover:shadow-md transition"
                onClick={() => navigate(`/admin/company/${c.id}/review`)}>
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-800">{c.companyName}</h3>
                      {c.organizationType && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold">
                          {c.organizationType}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold
                        ${statusBadge[c.status] || 'bg-gray-100 text-gray-600'}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                      <span>{c.contactPersonName}</span>
                      {c.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {c.city}{c.state ? `, ${c.state}` : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{c.contactPhone}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}